import React, { useEffect, useMemo, useState } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import { DefaultTable } from '@hud/web-components/Table/Table';
import { formatDatetime, renderDatetime } from '@hud/web-components/Table/CellRenderers';
import TableContainer from '@hud/web-components/Container/TableContainer';
import { Search } from '@hud/web-components/Search/Search';
import { MessageQueue } from '@hud/portal-dtos/webapp-message-queue.dto';
import Pagination from '@hud/web-components/Pagination/Pagination';
import {
  entitiesCellFormatter,
  entitiesCellRenderer,
} from '../entitiesCellRenderer/entitiesCellRenderer';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import renderTrendHeader from '../trendHeaderRenderer/trendHeaderRenderer';
import renderMetricTrend, {
  formatMetricTrend,
} from '../metricTrendRenderer/metricTrendRenderer';
import renderFunctionMetric, {
  formatFunctionMetric,
} from '../functionMetricCellRenderer/functionMetricCellRenderer';
import _ from 'lodash';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import {
  ClearAllButton,
  FiltersIcon,
  FiltersMenuButton,
} from '../FunctionsTable/FunctionsTable.styles';
import Menu from '@hud/web-components/Menu/Menu';
import tooltips from '../../assets/tooltips';
import renderMessageQueueName from '../MessageQueueNameCellRenderer/MessageQueueNameCellRenderer';
import { BreadcrumbItem } from '@hud/web-components/Breadcrumbs/Breadcrumbs';
import ServicesFilter from '../ServicesFilter/ServicesFilter';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { ThresholdFilter as ThresholdFilterComponent } from '@hud/web-components/Filters/ThresholdFilter';
import { ThresholdFilter, TimeFrame } from '@hud/portal-dtos/webapp-common.dto';
import { formatFlowType } from '../../utils';
import {
  DEFAULT_PAGE_SIZE,
  DURATION_AVG_DEFAULT,
  NORMALIZED_RUNS_DEFAULT,
  ERROR_RATE_DEFAULT,
  DURATION_AVG_PRESETS,
  NORMALIZED_RUNS_PRESETS,
  ERROR_RATE_PRESETS,
} from '../../consts';
import p90RenderFunctionMetric from '../functionMetricCellRenderer/p90MetricCellRenderer';
import renderRunsMetric from '../metricCellRenderer/runsCellRenderer';

const columnHelper = createColumnHelper<MessageQueue>();

export interface MessageQueuesTableProps {
  queues: MessageQueue[];
  total?: number;
  filtered?: number;
  loading: boolean;
  timeFrame: TimeFrame;
  searchValue: string;
  onSearchChange: (searchValue: string) => void;

  sortingState: SortingState;
  onSortingChange: (updater: Updater<SortingState>) => void;

  pages: number;
  currentPage: number;
  onPageChange: (page: number) => void;

  filteredServices: number[];
  onFilteredServicesChange: (value: number[]) => void;
  referralBreadcrumbs?: BreadcrumbItem[] | undefined;
  filteredDurationAvg?: ThresholdFilter;
  onFilteredDurationAvgChange?: (value: ThresholdFilter | undefined) => void;
  filteredNormalizedRuns?: ThresholdFilter;
  onFilteredNormalizedRunsChange?: (value: ThresholdFilter | undefined) => void;
  filteredErrorRate?: ThresholdFilter;
  onFilteredErrorRateChange?: (value: ThresholdFilter | undefined) => void;
  onClearAllFilters: () => void;

  fullPageTable?: boolean;
}

type FilterType = 'service' | 'durationAvg' | 'normalizedRuns' | 'errorRate';

interface Filter {
  type: FilterType;
  // Additional properties that each filter might need for its specific configuration
  props?: unknown; // Use more specific types based on actual requirements
}

export default function MessageQueuesTable({
  queues,
  loading,
  total = 0,
  filtered = 0,
  searchValue,
  onSearchChange,
  sortingState,
  onSortingChange,
  pages,
  currentPage,
  onPageChange,
  filteredServices,
  onFilteredServicesChange,
  referralBreadcrumbs,
  filteredDurationAvg,
  onFilteredDurationAvgChange,
  filteredNormalizedRuns,
  onFilteredNormalizedRunsChange,
  filteredErrorRate,
  onFilteredErrorRateChange,
  onClearAllFilters,
  fullPageTable,
  timeFrame,
}: MessageQueuesTableProps) {
  const { userPreferredServices } = useUserPreferences();

  const initializeFilters = (): Filter[] => {
    const initialFilters: Filter[] = [];

    if (filteredServices && filteredServices?.length > 0) {
      initialFilters.push({
        type: 'service',
        props: { ids: filteredServices },
      });
    }
    if (filteredDurationAvg) {
      initialFilters.push({
        type: 'durationAvg',
        props: { durationAvg: filteredDurationAvg },
      });
    }
    if (filteredNormalizedRuns) {
      initialFilters.push({
        type: 'normalizedRuns',
        props: { normalizedRuns: filteredNormalizedRuns },
      });
    }
    if (filteredErrorRate) {
      initialFilters.push({
        type: 'errorRate',
        props: { errorRate: filteredErrorRate },
      });
    }
    return initialFilters;
  };

  const [activeFilters, setActiveFilters] = useState<Filter[]>(initializeFilters);

  const handleFilterChange = (
    filterType: FilterType,
    isActive: boolean,
    filterProps = {},
  ) => {
    if (isActive) {
      if (activeFilters.some((f) => f.type === filterType)) return;
      setActiveFilters((prev) => [
        ...prev.filter((f) => f.type !== filterType),
        { type: filterType, ...filterProps },
      ]);
    } else {
      setActiveFilters((prev) => prev.filter((filter) => filter.type !== filterType));
    }
  };

  useEffect(() => {
    setActiveFilters(initializeFilters());
  }, [filteredServices]);

  const toggleServiceFilter = (isActive: boolean) => {
    handleFilterChange('service', isActive, { ids: filteredServices });
  };

  const toggleDurationAvgFilter = (isActive: boolean) => {
    handleFilterChange('durationAvg', isActive, {
      durationAvg: DURATION_AVG_DEFAULT,
    });
    if (isActive && !activeFilters.some((f) => f.type === 'durationAvg')) {
      onFilteredDurationAvgChange?.(DURATION_AVG_DEFAULT);
    }
  };

  const toggleNormalizedRunsFilter = (isActive: boolean) => {
    handleFilterChange('normalizedRuns', isActive, {
      normalizedRuns: NORMALIZED_RUNS_DEFAULT,
    });
    if (isActive && !activeFilters.some((f) => f.type === 'normalizedRuns')) {
      onFilteredNormalizedRunsChange?.(NORMALIZED_RUNS_DEFAULT);
    }
  };

  const toggleErrorRateFilter = (isActive: boolean) => {
    handleFilterChange('errorRate', isActive, {
      errorRate: ERROR_RATE_DEFAULT,
    });
    if (isActive && !activeFilters.some((f) => f.type === 'errorRate')) {
      onFilteredErrorRateChange?.(ERROR_RATE_DEFAULT);
    }
  };

  const topicsWithMultipleGroups = useMemo(() => {
    const groups = _.groupBy(queues, 'topicName');
    return Object.keys(groups).filter((key) => groups[key].length > 1);
  }, [queues]);

  const showE2EGraph = useMemo(() => {
    return queues.some(
      (queue) =>
        queue.type === 'KAFKA' ||
        (queue.metrics.consumeDurationAvg && queue.metrics.consumeDurationAvg.value > 0),
    );
  }, [queues]);

  const showMsgCount = useMemo(() => {
    return queues.some(
      (queue) => queue.metrics.msgCount && queue.metrics.msgCount.value > 0,
    );
  }, [queues]);

  const columns = useMemo(() => {
    const defaultColumns = [
      columnHelper.accessor('services', {
        header: 'Service',
        cell: entitiesCellRenderer('services'),
        size: 100,
        maxSize: 200,
        minSize: 110,
        meta: {
          fitFormatter: entitiesCellFormatter('services'),
          disableAutoFit: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.SERVICE,
        },
      }),
      columnHelper.accessor('queueName', {
        header: 'Title',
        cell: renderMessageQueueName(topicsWithMultipleGroups, referralBreadcrumbs),
        meta: {
          copyable: true,
          fitFormatter: (cell) => cell.getValue(),
          disableAutoFit: true,
        },
        minSize: 150,
        size: 250,
      }),
      columnHelper.accessor('type', {
        header: 'Type',
        cell: (cell) => formatFlowType(cell.getValue()),
        size: 80,
        minSize: 80,
        meta: {
          description: tooltips.MESSAGE_QUEUES_TABLE.TYPE,
        },
      }),
      columnHelper.accessor('metrics.runs.value', {
        id: 'runs.value',
        header: 'Runs (Runs/Day)',
        cell: renderRunsMetric(timeFrame),
        minSize: timeFrame === 'hour' ? 120 : 200,
        maxSize: 250,
        meta: {
          fitFormatter: formatFunctionMetric('number'),
          description: tooltips.MESSAGE_QUEUES_TABLE.RUNS,
          rightAlign: true,
        },
      }),
      columnHelper.accessor('metrics.runs', {
        id: 'runs.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 40,
        minSize: 40,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.RUNS_TREND,
        },
      }),
      columnHelper.accessor('metrics.durationAvg.value', {
        id: 'durationAvg.value',
        header: 'Processing Dur Avg',
        cell: renderFunctionMetric('duration'),
        size: 130,
        maxSize: 200,
        minSize: 100,
        meta: {
          fitFormatter: formatFunctionMetric('duration'),
          rightAlign: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.PROCESSING_DURATION,
        },
      }),
      columnHelper.accessor('metrics.durationAvg', {
        id: 'durationAvg.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 40,
        minSize: 40,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.AVG_PROCESSING_DURATION_TREND,
        },
      }),
      columnHelper.accessor('metrics.durationP90.value', {
        id: 'durationP90.value',
        header: 'Processing Dur P90',
        cell: p90RenderFunctionMetric('duration'),
        size: 130,
        maxSize: 200,
        minSize: 100,
        meta: {
          fitFormatter: formatFunctionMetric('duration'),
          rightAlign: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.PROCESSING_DURATION_P90,
        },
      }),
      columnHelper.accessor('metrics.durationP90', {
        id: 'durationP90.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 40,
        minSize: 40,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.PROCESSING_P90_DURATION_TREND,
        },
      }),
      columnHelper.accessor('metrics.normalizedMsgCount.value', {
        id: 'msgCount.value',
        header: 'Messages/d',
        cell: renderFunctionMetric('numberNullZero'),
        size: 152,
        minSize: 120,
        maxSize: 200,
        meta: {
          fitFormatter: formatFunctionMetric('number'),
          rightAlign: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.MSG_COUNT,
        },
      }),
      columnHelper.accessor('metrics.errorRate.value', {
        id: 'errorRate.value',
        header: 'Error rate',
        cell: renderFunctionMetric('percent'),
        size: 130,
        minSize: 100,
        maxSize: 200,
        meta: {
          fitFormatter: formatFunctionMetric('duration'),
          rightAlign: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.ERRORS,
        },
      }),
      columnHelper.accessor('metrics.errorRate', {
        id: 'errorRate.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 40,
        minSize: 40,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.ERRORS_TREND,
        },
      }),
      columnHelper.accessor('lastSeenAt', {
        header: 'Last invocation',
        cell: renderDatetime(true),
        size: 160,
        maxSize: 200,
        meta: {
          fitFormatter: formatDatetime(true),
          rightAlign: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.LAST_INVOKED,
        },
      }),
    ];
    const consumeDurationColumns = [
      // NOTE!!!
      // the SDK sends the durations under different naming logic: the 'duration' metric is de-facto the consumer function duration after the message is pulled from the queue,
      // while the 'consume_duration' metric is the total e2e duration from the moment a message enters the queue until it is fully consumed.
      // This means that the 'consume_duration' is always longer than the 'duration'. This is confusing, so in the front end side we rename them to:
      // 'consume_duration' Metric in the BE -> 'e2e_duration' in the FE
      // 'duration' Metric in the BE -> 'consumer_duration' in the FE
      columnHelper.accessor('metrics.consumeDurationAvg.value', {
        id: 'consumeDurationAvg.value',
        header: 'E2E Dur Avg',
        cell: renderFunctionMetric('duration'),
        size: 130,
        maxSize: 200,
        minSize: 100,
        meta: {
          fitFormatter: formatFunctionMetric('duration'),
          rightAlign: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.E2E_DURATION,
        },
      }),
      columnHelper.accessor('metrics.consumeDurationAvg', {
        id: 'consumeDurationAvg.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 40,
        minSize: 40,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.AVG_E2E_DURATION_TREND,
        },
      }),
      columnHelper.accessor('metrics.consumeDurationP90.value', {
        id: 'consumeDurationP90.value',
        header: 'E2E Dur P90',
        cell: p90RenderFunctionMetric('duration'),
        size: 130,
        maxSize: 200,
        minSize: 100,
        meta: {
          fitFormatter: formatFunctionMetric('duration'),
          rightAlign: true,
          description: tooltips.MESSAGE_QUEUES_TABLE.E2E_DURATION_P90,
        },
      }),
    ];
    // Find the index of the Messages/d column
    const msgCountColIndex = defaultColumns.findIndex(
      (col) => col.id === 'msgCount.value',
    );
    // Remove the Messages/d column by default
    if (!showMsgCount) {
      defaultColumns.splice(msgCountColIndex, 1);
    }
    if (showE2EGraph) {
      defaultColumns.splice(5, 0, ...consumeDurationColumns);
    }
    return defaultColumns;
  }, [topicsWithMultipleGroups, referralBreadcrumbs]);

  const table = useReactTable({
    data: queues,
    columns: columns,
    state: {
      sorting: sortingState,
    },

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),

    onSortingChange,

    columnResizeMode: 'onChange',
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    enableSortingRemoval: false,

    defaultColumn: {
      minSize: 130,
    },
  });

  const hasFilters = activeFilters.length > 0;

  return (
    <TableContainer.Container
      title={loading ? <Skeleton width={140} /> : `Showing ${filtered}/${total}`}
    >
      <TableContainer.Header>
        <TableContainer.Filters>
          <Search inline value={searchValue} onSearchChange={onSearchChange} />
          {activeFilters.map((filter) => {
            switch (filter.type) {
              case 'service':
                return (
                  <ServicesFilter
                    userPreferredServices={userPreferredServices ?? []}
                    filteredServices={filteredServices}
                    onFilteredServicesChange={onFilteredServicesChange}
                    handleFilterChange={handleFilterChange}
                  />
                );
              case 'durationAvg':
                return (
                  <ThresholdFilterComponent
                    key={filter.type}
                    label="Duration Avg"
                    value={filteredDurationAvg?.value ?? 0}
                    operator={filteredDurationAvg?.operator ?? 'gte'}
                    onOperatorChange={(operator) => {
                      onFilteredDurationAvgChange?.({
                        ...(filteredDurationAvg as ThresholdFilter),
                        operator,
                      });
                    }}
                    onValueChange={(value) => {
                      onFilteredDurationAvgChange?.({
                        ...(filteredDurationAvg as ThresholdFilter),
                        value,
                      });
                    }}
                    presetValues={DURATION_AVG_PRESETS}
                    unit="ms"
                    onClear={() => {
                      onFilteredDurationAvgChange?.(undefined);
                      toggleDurationAvgFilter(false);
                    }}
                  />
                );
              case 'normalizedRuns':
                return (
                  <ThresholdFilterComponent
                    key={filter.type}
                    label="Runs/d"
                    value={filteredNormalizedRuns?.value ?? 0}
                    operator={filteredNormalizedRuns?.operator ?? 'gte'}
                    onOperatorChange={(operator) => {
                      onFilteredNormalizedRunsChange?.({
                        ...(filteredNormalizedRuns as ThresholdFilter),
                        operator,
                      });
                    }}
                    onValueChange={(value) => {
                      onFilteredNormalizedRunsChange?.({
                        ...(filteredNormalizedRuns as ThresholdFilter),
                        value,
                      });
                    }}
                    presetValues={NORMALIZED_RUNS_PRESETS}
                    onClear={() => {
                      onFilteredNormalizedRunsChange?.(undefined);
                      toggleNormalizedRunsFilter(false);
                    }}
                  />
                );
              case 'errorRate':
                return (
                  <ThresholdFilterComponent
                    key={filter.type}
                    label="Failing"
                    value={filteredErrorRate?.value ?? 0}
                    operator={filteredErrorRate?.operator ?? 'gte'}
                    onOperatorChange={(operator) => {
                      onFilteredErrorRateChange?.({
                        ...(filteredErrorRate as ThresholdFilter),
                        operator,
                      });
                    }}
                    onValueChange={(value) => {
                      onFilteredErrorRateChange?.({
                        ...(filteredErrorRate as ThresholdFilter),
                        value,
                      });
                    }}
                    presetValues={ERROR_RATE_PRESETS}
                    unit="%"
                    onClear={() => {
                      onFilteredErrorRateChange?.(undefined);
                      toggleErrorRateFilter(false);
                    }}
                  />
                );
              default:
                return null;
            }
          })}
          <Menu.Menu>
            <Menu.Target>
              <div>
                <FiltersMenuButton>
                  <FiltersIcon
                    icon={hasFilters ? 'menu.plus' : 'other.filters'}
                    size={hasFilters ? '14px' : '20px'}
                  />
                  {!hasFilters ? 'Filters' : ''}
                </FiltersMenuButton>
              </div>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => toggleServiceFilter(true)}>Services</Menu.Item>
              <Menu.Divider />
              <Menu.Item onClick={() => toggleNormalizedRunsFilter(true)}>
                Invoked often
              </Menu.Item>
              <Menu.Item onClick={() => toggleDurationAvgFilter(true)}>
                Exceeding duration
              </Menu.Item>
              <Menu.Item onClick={() => toggleErrorRateFilter(true)}>Failing</Menu.Item>
            </Menu.Dropdown>
          </Menu.Menu>
          {hasFilters ? (
            <ClearAllButton
              onClick={() => {
                toggleServiceFilter(false);
                toggleDurationAvgFilter(false);
                toggleNormalizedRunsFilter(false);
                toggleErrorRateFilter(false);

                onClearAllFilters();
              }}
            >
              Clear All
            </ClearAllButton>
          ) : null}
        </TableContainer.Filters>
      </TableContainer.Header>
      <HorizontalContainer>
        <DefaultTable
          table={table}
          label="Message Queues Table"
          loading={loading}
          loadingRows={DEFAULT_PAGE_SIZE}
          options={{
            emptyStateProps: {
              icon: 'entities.messageQueue',
              expanded: fullPageTable,
            },
          }}
        />
      </HorizontalContainer>
      <TableContainer.Footer>
        <Pagination
          pages={pages}
          currentPage={currentPage}
          onPageChange={onPageChange}
          loading={loading}
        />
      </TableContainer.Footer>
    </TableContainer.Container>
  );
}
